import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AuthenticationService} from '../authentication.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Observable, of, Subject} from 'rxjs';
import {LogoService} from './logo.service';
import {distinctUntilChanged, filter, map, mergeMap, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {PermissionService} from '../permission.service';
import {ConfirmDialogService} from '../../shared/confirm-dialog.service';
import {ActivatedRoute, Data, NavigationEnd, Router} from '@angular/router';
import {marker} from '@biesbjerg/ngx-translate-extract-marker';
import {LabelingService} from '../../shared/labeling.service';
import {HelpdeskTicketComponent} from '../helpdesk-ticket/helpdesk-ticket.component';
import {HelpersService} from 'src/app/shared/helpers.service';
import {HttpCancelService} from '../httpcancel.service';
import {GenericDialogService} from '../../shared/generic-dialog.service';
import {IOrganizationData} from "../organization";
import {WhatsNewDisplayComponent} from "../../whats-new/whats-new-display/whats-new-display.component";
import {MatDialog} from "@angular/material/dialog";
import {WhatsNewContentService} from "../../whats-new/whats-new-content.service";
import {FeatureTogglesService} from '../feature-toggles.service';
import {LookerEmbedService} from '../../reporting/looker-dashboards/looker-embed.service';
import {isBypLookerOrganization} from '../../reporting/looker-dashboards/looker-dashboards.config';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    standalone: false
})
export class NavbarComponent implements OnInit, OnDestroy {

  organizationForm: FormGroup;
  organizationData: IOrganizationData;
  orgId = 3;
  @Input() whatsNewHighlighting;
  @Input() whatsNewMenuDisplay;

  private destroyed$: Subject<void> = new Subject<void>();
  @Output() public readonly openMenu: EventEmitter<void> = new EventEmitter<void>();
  @Output() whatsNewSectionOpened = new EventEmitter();

  public loggedInMessage$ = this.translateService.stream(marker('Logged in as'))
    .pipe(
      map(loggedInAs =>
        `${loggedInAs}: ${this.authenticationService.currentUser?.firstName} ${this.authenticationService.currentUser?.lastName}
         (${this.authenticationService.currentUser?.email})`,
      ),
    );

  private routeData: Data;
  storeCompletedLabel: Observable<any>;

  constructor(
    private fb: FormBuilder,
    private confirmDialog: ConfirmDialogService,
    public router: Router,
    private route: ActivatedRoute,
    public authenticationService: AuthenticationService,
    public logoService: LogoService,
    public translateService: TranslateService,
    public labelingService: LabelingService,
    public permissionService: PermissionService,
    public featureTogglesService: FeatureTogglesService,
    public helpersService: HelpersService,
    private httpCancelService: HttpCancelService,
    private genericDialogService: GenericDialogService,
    private whatsNewService: WhatsNewContentService,
    private matDialog: MatDialog,
    private lookerEmbedService: LookerEmbedService,
  ) {
    setTimeout(() => {
      this.authenticationService.isLoggedIn = Boolean(localStorage.getItem('isLoggedIn'));
    }, 1);

    this.storeCompletedLabel = this.labelingService.getStoreSentenceLabel(marker('Store Completed'), marker('Completed'));
  }

  private subscribeToRouteData(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.route),
      map(route => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      map(route => route.snapshot.data),
    ).subscribe(data => this.routeData = data);
  }

  ngOnInit() {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user !== null) {
      this.authenticationService.organizations = user.organizations;
    }

    this.organizationForm = this.fb.group({
      activeOrganization: [this.authenticationService.selectedOrganization, Validators.required],
    });

    this.organizationData = this.authenticationService.organizationData;

    this.initOrganization();
    this.onOrganizationChange();
    this.checkActiveOrganizationStatus();
    this.subscribeToRouteData();

    this.authenticationService.organizationDataUpdatedEvent.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.organizationData = this.authenticationService.organizationData;
    });

  }

  onCreateHelpdeskTicket() {
    this.genericDialogService.open(HelpdeskTicketComponent);
  }

  viewWhatsNew() {
    this.matDialog.open(WhatsNewDisplayComponent, {
      height: 'fit-content',
      width: '1000px',
      autoFocus: false,
      panelClass: 'dialog--no-padding',
    }).afterClosed().subscribe(() =>
    {
      this.whatsNewHighlighting = false;
      this.whatsNewSectionOpened.emit();
    });
  }

  private initOrganization(): void {
    this.organizationForm.get('activeOrganization').valueChanges
      .pipe(
        filter(organizationId => !!organizationId),
        switchMap(organizationId => this.authenticationService.setActiveOrganization(organizationId)),
        take(1)
      )
      .subscribe();
  }

  checkActiveOrganizationStatus() {
    this.authenticationService.activeOrganizationInfo$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
      )
      .subscribe(value => {
        this.orgId = value;
        this.organizationForm.get('activeOrganization').setValue(this.orgId);
      });
  }

  onOrganizationChange() {
    this.organizationForm.get('activeOrganization').valueChanges
      .pipe(
        filter(organizationId => !!organizationId),
        distinctUntilChanged(),
        filter(value => value !== this.orgId),
        mergeMap((organizationId) => {
          this.authenticationService.changeOrganizationEvent.next(organizationId);

          if (this.routeData?.currentPage === 'formBuilder') {
            this.httpCancelService.cancelPendingRequests();
            return of(false);
          }

          if (this.routeData?.obsoletedByOrganizationSwitch) {
            return this.confirmDialog.openConfirmDialog(this.translateService.stream(marker('Any unsaved changes will be lost.')))
              .pipe(
                mergeMap(confirm => {
                  if (confirm) {
                    this.httpCancelService.cancelPendingRequests();
                    return this.authenticationService.setActiveOrganization(organizationId).pipe(
                      mergeMap(() => this.router.navigate(['/'])),
                      take(1),
                    );
                  } else {
                    return of(false);
                  }
                }),
              );
          } else {
            return this.authenticationService.setActiveOrganization(organizationId)
              .pipe(take(1));
          }
        }),
        tap(() => this.organizationForm.get('activeOrganization').setValue(this.orgId)),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  logout() {
    this.authenticationService.logoutEvent.next(true);

    if (this.routeData?.currentPage === 'formBuilder') {
      // in this case logout will be treated inside builder.component.ts
      return;
    } else {
      if (this.routeData?.mightHavePendingChanges) {
        this.confirmDialog.openConfirmDialog(this.translateService.stream(marker('Any unsaved changes will be lost.')))
          .pipe(
            tap(confirm => {
              if (confirm) {
                this.authenticationService.logout();
              }
            }),
          ).subscribe();
      } else {
        this.authenticationService.logout();
      }
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getRouterLink() {
    return this.authenticationService.showNavigation() ? '/home' : null;
  }

  /**
   * Navigates to a route and forces a refresh of the component
   */
  navigateAndRefresh(url: string): void {
    // Check if we're already on the same route
    if (this.router.url === url) {
      // Force refresh by navigating to a dummy route and back
      this.router.navigateByUrl('/dummy', { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(url);
      });
    } else {
      // Navigate to the new route
      this.router.navigateByUrl(url);
    }
  }

  prefetchLookerSession(dashboardRouteKey?: string): void {
    const orgId = localStorage.getItem('selectedOrganization');
    if (!orgId) {
      return;
    }

    this.permissionService.hasPermission('GRAFANA').pipe(take(1)).subscribe((hasPermission) => {
      if (hasPermission) {
        this.lookerEmbedService.warmCookielessSession(orgId, { dashboardRouteKey });
      }
    });
  }

  showBypLookerDashboards(): boolean {
    return isBypLookerOrganization(
      localStorage.getItem('selectedOrganization') ?? this.authenticationService.selectedOrganization,
    );
  }

  showStandardLookerDashboards(): boolean {
    return !this.showBypLookerDashboards();
  }
}
